/**
 * Client-side JWT claim helpers.
 *
 * The admin panel only ever *reads* the access token to branch the UI (e.g.
 * show an admin's merchant picker vs. a merchant's own catalog). It does NOT
 * trust these claims for authorization — every endpoint is enforced
 * server-side. Decoding is best-effort: a malformed/absent token yields
 * `null`/empty rather than throwing.
 *
 * Keycloak realm roles arrive under `realm_access.roles` (e.g. `admin`,
 * `merchant`, `courier`, `customer`); the subject (`sub`) is the user's UUID.
 */
import { getAccessToken } from './tokens';

export interface JwtClaims {
  sub?: string;
  realm_access?: { roles?: string[] };
  [key: string]: unknown;
}

/** Decode a base64url segment to a UTF-8 string, or `null` on failure. */
function base64UrlToString(segment: string): string | null {
  try {
    const padLength = segment.length % 4 === 0 ? 0 : 4 - (segment.length % 4);
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat(padLength);
    const binary = atob(base64);
    const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch {
    return null;
  }
}

/** Decode the payload of a JWT without verifying its signature. */
export function decodeJwt(token: string | null): JwtClaims | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length < 2 || !parts[1]) return null;
  const json = base64UrlToString(parts[1]);
  if (!json) return null;
  try {
    const parsed: unknown = JSON.parse(json);
    return parsed && typeof parsed === 'object' ? (parsed as JwtClaims) : null;
  } catch {
    return null;
  }
}

/** Claims for the currently stored access token (or `null`). */
export function getClaims(): JwtClaims | null {
  return decodeJwt(getAccessToken());
}

export function getRealmRoles(claims: JwtClaims | null): string[] {
  const roles = claims?.realm_access?.roles;
  return Array.isArray(roles) ? roles.filter((r): r is string => typeof r === 'string') : [];
}

export function getUserId(claims: JwtClaims | null): string | null {
  return typeof claims?.sub === 'string' ? claims.sub : null;
}

export function hasRealmRole(role: string, claims: JwtClaims | null): boolean {
  return getRealmRoles(claims).includes(role);
}

export const isAdmin = (claims: JwtClaims | null): boolean => hasRealmRole('admin', claims);

export const isMerchant = (claims: JwtClaims | null): boolean => hasRealmRole('merchant', claims);
